import cx from 'classnames';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import { isDesktop, isMobile, isTablet } from 'react-device-detect';
import Select from 'react-select';
import CustomDatePickerInput from '../../../components/other/CustomDatePickerInput';
import { DATE_PICKER_DATE_FORMAT } from '../../../constants';
import { useAuth } from '../../../container/authContext';
import { usePermission } from '../../../container/permissionContext';
import useOutsideClick from '../../../hook/useOutsideClick';
import Layout from '../../../layout/default';
import {
  addUnitRoleService,
  cancelPostedShiftsService,
  getAvailableFacilitiesService,
  getPeriodByIdService,
  getPeriodService,
  postShiftsService,
  resetScheduleService,
  updateUnitRoleService,
  getAssignmentRequests,
  getAvailableEmployeesService,
  bulkCreateSchedulingCellAssignmentsService,
  bulkDeleteSchedulingCellAssignmentsService,
  bulkOffDueSchedulingCellAssignmentsService,
  getSchedulingPeriodLastModifiedService,
  getPeriodByIdSilentService,
} from '../../../services/scheduling';
import { convertTimeRangeTo12Hour, getDateFromString } from '../../../utility';
import { isResponseOk } from '../../../utility/isResponseOk';
import PageLoader from './../../../components/other/PageLoader';
import AssignmentRequestsModal from './components/AssignmentRequestsModal';
import { countShiftRequests } from './components/AssignmentRequestsModal/shiftRequests';
import CancelPostedShiftsModal from './components/CancelAllPostedShiftsModal';
import CellAssignmentSidebar from './components/CellAssignmentSidebar';
import EmptySchedulingPeriod from './components/EmptySchedulingPeriod';
import ExportRecurringScheduleModal from './components/ExportRecurringScheduleModal';
import ExportScheduleToPdfModal from './components/ExportScheduleToPdfModal';
import Header from './components/Header';
import ResetScheduleModal from './components/ResetScheduleModal';
import SchedulingDay from './components/SchedulingDay';
import SchedulingUnitRow from './components/SchedulingUnitRow';
import './styles.scss';
import SchedulingDayNotes from './components/SchedulingDayNotes';
import NotificationSettingsModal from './components/NotificationSettingsModal';
import PostScheduleModal from './components/PostScheduleModal';
import PostShiftsSidebar from './components/PostShiftsSidebar';
import PrintWeeklyScheduleConfirmModal from './components/PrintWeeklyScheduleConfirmModal';
import PrintWeeklyScheduleModal from './components/PrintWeeklyScheduleModal';
import PrintEmployeeScheduleSelectModal from './components/PrintEmployeeScheduleSelectModal';
import PrintEmployeeScheduleModal from './components/PrintEmployeeScheduleModal';
import { getRoleLabelsService } from '../../../services/company';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import EditSidebar from './components/EditSidebar';
import DateCalendar from '../../../components/calendars/DateCalendar/DateCalendar';
const SchedulingPage = () => {
  const datePickerInputRef = useRef(null);
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const scheduleActionRef = useRef();
  const { user = {} } = useAuth();
  const assignmentRequestModalRef = useRef();
  const { hasPermissionByCodeName, isSuperAdmin } = usePermission();
  const hasWritePermission = hasPermissionByCodeName('scheduling_management');
  const { company: { id: companyID } = {} } = user;

  const editSidebarInitialFormData = {
    halfPart: { value: 'both_halves', label: 'Full shift' },
    notes: '',
    fontColor: '#101828',
    cellColor: '#ffffff',
    hours: { value: 8, label: '8' },
    minutes: { value: 0, label: '0' },
    offDueTo: { value: 'pto', label: 'PTO' },
  };

  const [filtersData, setFiltersData] = useState({
    facility: null,
    date: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [availableFacilities, setAvailableFacilities] = useState([]);
  const [periodData, setPeriodData] = useState({});
  const [skipRefetching, setSkipRefetching] = useState(false);
  const [isSelectingPostShiftsActive, setIsSelectingPostShiftsActive] = useState(false);
  const [selectedShiftsToPost, setSelectedShiftsToPost] = useState([]);
  const [printScheduleData, setPrintScheduleData] = useState(null);
  const [isEmptySchedulingPeriod, setIsEmptySchedulingPeriod] = useState(false);
  const [scheduleActionDropdownOpen, setScheduleActionDropdownOpen] = useState(false);
  const [isOpenResetScheduleModal, setIsOpenResetScheduleModal] = useState(false);
  const [showRecurringShifts, setShowRecurringShifts] = useState(false);
  const [isExportRecurringShiftsModalOpen, setIsExportRecurringShiftsModalOpen] = useState(false);
  const [isOpenCancelPostedShiftsModal, setIsOpenCancelPostedShiftsModal] = useState(false);
  const [cellAssignmentData, setCellAssignmentData] = useState();
  const [assignmentRequestsModalData, setAssignmentRequestsModalData] = useState({});
  const [isNotificationSettingsModalOpen, setIsNotificationSettingsModalOpen] = useState(false);
  const [isPostScheduleModalOpen, setIsPostScheduleModalOpen] = useState(false);
  const [isPrintScheduleConfirmModalOpen, setIsPrintScheduleModalConfirmOpen] = useState(false);
  const [printScheduleDays, setPrintScheduleDays] = useState([]);
  const [isPrintEmployeeScheduleSelectOpen, setIsPrintEmployeeScheduleSelectOpen] = useState(false);
  const [printEmployeeSchedule, setPrintEmployeeSchedule] = useState(null);
  const [roleLabels, setRoleLabels] = useState({});
  const [employees, setEmployees] = useState([]);

  // Edit sidebar states
  const [isEditSidebarOpen, setIsEditSidebarOpen] = useState(false);
  const [editSidebarSelectedUser, setEditSidebarSelectedUser] = useState(null);
  const [editSidebarAction, setEditSidebarAction] = useState(null);
  const [editSidebarFormData, setEditSidebarFormData] = useState(editSidebarInitialFormData);
  const [editSidebarSelectedItems, setEditSidebarSelectedItems] = useState([]);

  const facilityId = useMemo(() => {
    return new URLSearchParams(window.location.search).get('facility_id');
  }, []);

  useEffect(() => {
    getRoleLabelsService(user?.company?.id).then((res) => {
      setRoleLabels(res.data);
    });
  }, [user?.company?.id]);

  useOutsideClick(scheduleActionRef, () => setScheduleActionDropdownOpen(false));

  const isPastPeriod = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    return periodData.end_date && new Date(periodData.end_date).setHours(0, 0, 0, 0) < now;
  }, [periodData]);

  const updateAssignmentRequests = useCallback(async () => {
    try {
      const unapprovedCells = [];
      periodData.units.forEach((unit) => {
        unit.roles.forEach((role) => {
          role.cells.forEach((cell) => {
            if (cell.assignment_requests?.length) {
              unapprovedCells.push(cell.id);
            }
          });
        });
      });
      const params = {
        cell_ids: unapprovedCells,
      };
      const response = await getAssignmentRequests(periodData.id, params);

      if (isResponseOk(response) && response.data) {
        const cellsAssignmentRequests = {};
        response.data.assignment_requests.forEach((assignmentRequest) => {
          if (!cellsAssignmentRequests[assignmentRequest.scheduling_cell_id]) {
            cellsAssignmentRequests[assignmentRequest.scheduling_cell_id] = [assignmentRequest];
          } else {
            cellsAssignmentRequests[assignmentRequest.scheduling_cell_id].push(assignmentRequest);
          }
        });

        setPeriodData((state) => {
          if (state && state.id) {
            return {
              ...state,
              units: state.units.map((unit) => ({
                ...unit,
                roles: unit.roles.map((role) => ({
                  ...role,
                  cells: role.cells.map((cell) => {
                    if (response.data.unapproved_cells.some((unapprovedCell) => unapprovedCell.id === cell.id)) {
                      return response.data.unapproved_cells.find((unapprovedCell) => unapprovedCell.id === cell.id);
                    }

                    if (cellsAssignmentRequests[cell.id]) {
                      return {
                        ...cell,
                        assignment_requests: cellsAssignmentRequests[cell.id],
                      };
                    }

                    return cell;
                  }),
                })),
              })),
            };
          } else {
            return state;
          }
        });
      }
    } catch (e) {
      console.error(e);
    }
  }, [periodData]);

  useEffect(() => {
    (async () => {
      try {
        const response = await getAvailableEmployeesService(periodData.id);
        if (isResponseOk(response) && response.data) {
          setEmployees(response.data);
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, [periodData?.id]);

  useEffect(() => {
    setEditSidebarSelectedItems([]);
  }, [editSidebarFormData.halfPart.value]);

  useEffect(() => {
    const interval = setInterval(() => {
      updateAssignmentRequests();
    }, 10000);

    return () => clearInterval(interval);
  }, [updateAssignmentRequests]);

  const lastModifiedVersionRef = useRef(null);
  const pollingPausedRef = useRef(false);

  pollingPausedRef.current =
    isEditSidebarOpen || !!cellAssignmentData?.unit || isSelectingPostShiftsActive || isLoading;

  useEffect(() => {
    lastModifiedVersionRef.current = null;
  }, [periodData?.id]);

  useEffect(() => {
    if (!periodData?.id) {
      return;
    }

    let cancelled = false;

    const checkForUpdates = async () => {
      if (pollingPausedRef.current) {
        return;
      }
      try {
        const versionResponse = await getSchedulingPeriodLastModifiedService(periodData.id);
        if (cancelled || !isResponseOk(versionResponse) || !versionResponse.data?.version) {
          return;
        }
        const newVersion = versionResponse.data.version;

        if (lastModifiedVersionRef.current === null) {
          lastModifiedVersionRef.current = newVersion;
          return;
        }
        if (lastModifiedVersionRef.current === newVersion) {
          return;
        }
        if (pollingPausedRef.current) {
          return;
        }

        const periodResponse = await getPeriodByIdSilentService(periodData.id);
        if (cancelled || !isResponseOk(periodResponse) || !periodResponse.data) {
          return;
        }
        if (pollingPausedRef.current) {
          return;
        }

        lastModifiedVersionRef.current = newVersion;
        setPeriodData((state) => {
          if (!state || state.id !== periodResponse.data.id) {
            return state;
          }
          return {
            ...periodResponse.data,
            start_date: getDateFromString(periodResponse.data.start_date),
            end_date: getDateFromString(periodResponse.data.end_date),
          };
        });
      } catch (e) {
        console.error(e);
      }
    };

    const interval = setInterval(checkForUpdates, 5000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [periodData?.id]);

  const handleAddUnitRole = useCallback(
    async (payload) => {
      try {
        const params = {};
        setIsLoading(true);
        const response = await addUnitRoleService(payload, params);
        if (isResponseOk(response) && response.data) {
          setPeriodData((state) => ({
            ...state,
            units: state.units.map((unit) => {
              if (unit.id === payload.scheduling_unit_id) {
                return {
                  ...unit,
                  roles: [...unit.roles, response.data],
                };
              }
              return unit;
            }),
          }));
        }
      } catch (e) {
        console.error(e);
      }
      setIsLoading(false);
    },
    [companyID],
  );

  const handleClickPrintSchedule = (dateStr) => {
    const formatDateStr = (date) => {
      const d = getDateFromString(date);
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const dayOfWeek = dayNames[d.getDay()].slice(0, 2);
      return `${month}/${day} ${dayOfWeek}`.toUpperCase();
    };

    const periodDay = periodData.days.find((day) => formatDateStr(day.date) === dateStr);

    const scheduleData = {
      units: periodData.units.map((unit) => ({
        ...unit,
        roles: unit.roles.map((role) => ({
          ...role,
          cells: role.cells.filter((cell) => formatDateStr(cell.day) === dateStr),
        })),
      })),
    };

    let shiftHours = 0;

    periodData.units.forEach((unit) => {
      unit.roles.forEach((role) => {
        role.cells.forEach((cell) => {
          if (cell.day === periodDay.date) {
            ['both_halves_user', 'first_half_user', 'second_half_user'].forEach((key) => {
              const user = cell[key];
              if (user && !user.is_off) shiftHours += user.hours + user.minutes / 60;
            });
          }
        });
      });
    });

    shiftHours = Math.round(shiftHours * 100) / 100;

    const totalCensus = periodData.units.reduce((sum, unit) => sum + (Number(unit.census_by_date?.[periodDay.date]) || 0), 0);

    const ppdValue = shiftHours > 0 && totalCensus > 0 ? parseFloat((shiftHours / totalCensus).toFixed(2)) : 0;

    setPrintScheduleData({
      dateStr,
      scheduleData,
      periodDay,
      ppdValue,
      totalCensus,
    });
  };

  const getPeriodData = useCallback(
    async (forceRefetch, forceScroll = true, skipScroll = false) => {
      if (!forceRefetch && periodData?.id) {
        const isDateInPeriod =
          new Date(filtersData.date).setHours(0, 0, 0, 0) >= new Date(periodData.start_date).setHours(0, 0, 0, 0) &&
          new Date(filtersData.date).setHours(0, 0, 0, 0) <= new Date(periodData.end_date).setHours(0, 0, 0, 0);

        if (isDateInPeriod) {
          return;
        }
      }

      if (!filtersData.facility) {
        return;
      }

      if (skipRefetching && !forceRefetch) {
        setSkipRefetching(false);
        return;
      }

      try {
        const params = {
          company_id: companyID,
          facility_id: filtersData.facility.value,
          date: filtersData.date ? dayjs(filtersData.date).format('YYYY-MM-DD') : null,
        };
        const periodResponse = await getPeriodService(params);
        if (isResponseOk(periodResponse) && periodResponse.data) {
          if (periodResponse.data.is_empty) {
            setIsEmptySchedulingPeriod(true);

            setPeriodData({
              ...periodResponse.data,
            });
          } else {
            const periodStartDate = getDateFromString(periodResponse.data.start_date);
            const periodEndDate = getDateFromString(periodResponse.data.end_date);

            setPeriodData({
              ...periodResponse.data,
              start_date: periodStartDate,
              end_date: periodEndDate,
            });
            setIsEmptySchedulingPeriod(false);
            let newFiltersDate = new Date(filtersData.date || periodStartDate);

            if (forceScroll) {
              const startShiftDate = new Date(periodStartDate).setHours(0, 0, 0, 0);
              const endShiftDate = new Date(periodEndDate).setHours(23, 59, 59, 999);
              if (new Date() >= startShiftDate && new Date() <= endShiftDate) {
                newFiltersDate = new Date();
              }
            }
            if (!filtersData.date) {
              setFiltersData((prevState) => {
                return {
                  ...prevState,
                  date: newFiltersDate,
                };
              });
            }
            if (!skipScroll) {
              const interval = setInterval(() => {
                const shiftElemId = `shift-date-${String(newFiltersDate ? newFiltersDate.getMonth() + 1 : periodStartDate).padStart(2, '0')}-${String(
                  newFiltersDate ? newFiltersDate.getDate() : periodStartDate,
                ).padStart(2, '0')}`;
                const element = document.getElementById(shiftElemId);
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                  clearInterval(interval);
                }
              }, 500);
            }
          }

          const url = new URL(window.location.href);
          url.searchParams.set('facility_id', filtersData.facility.value);

          window.history.replaceState({}, '', url);
        }
      } catch (e) {
        setPeriodData({});
        console.error(e);
      }
    },
    [filtersData.date, filtersData.facility, companyID],
  );

  const handleResetSchedule = useCallback(async () => {
    try {
      setIsLoading(true);
      const payload = {
        facility_id: filtersData.facility.value,
      };
      const response = await resetScheduleService(payload);
      if (isResponseOk(response)) {
        setIsOpenResetScheduleModal(false);
        setSkipRefetching(false);
        setFiltersData((prevState) => ({ ...prevState, date: null }));
        setScheduleActionDropdownOpen(false);
      }
    } catch (e) {
      console.error(e);
    }
    setIsLoading(false);
  }, [filtersData.facility, getPeriodData]);

  const handleCancelPostedShifts = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await cancelPostedShiftsService(periodData.id);
      if (isResponseOk(response)) {
        getPeriodData(true, false);
        setIsOpenCancelPostedShiftsModal(false);
      }
    } catch (e) {
      console.error(e);
    }
    setIsLoading(false);
  });

  const handleUpdateUnitRole = useCallback(
    async (roleId, payload) => {
      try {
        setIsLoading(true);
        const response = await updateUnitRoleService(roleId, payload);
        if (isResponseOk(response) && response.data) {
          setPeriodData((state) => ({
            ...state,
            units: state.units.map((unit) => {
              return {
                ...unit,
                roles: unit.roles.map((role) => {
                  if (role.id === roleId) {
                    return response.data;
                  }
                  return role;
                }),
              };
            }),
          }));
        }
      } catch (e) {
        console.error(e);
      }
      setIsLoading(false);
    },
    [companyID],
  );

  const handleCloseEditSidebar = () => {
    setIsEditSidebarOpen(false);
    setEditSidebarSelectedItems([]);
    setEditSidebarAction(null);
    setEditSidebarSelectedUser(null);
    setEditSidebarFormData(editSidebarInitialFormData);
  };

  const handleSaveEditSidebar = useCallback(async () => {
    try {
      setIsLoading(true);
      const payload = {
        scheduling_cell_ids: editSidebarSelectedItems.map((item) => item.cell.id),
        user_id: editSidebarSelectedUser.id,
        half_type: editSidebarFormData.halfPart.value,
        notes: editSidebarFormData.notes,
        font_color: editSidebarFormData.fontColor,
        cell_color: editSidebarFormData.cellColor,
        hours: editSidebarFormData.hours.value,
        minutes: editSidebarFormData.minutes.value,
      };

      const response = await bulkCreateSchedulingCellAssignmentsService(payload);
      if (isResponseOk(response)) {
        getPeriodData(true, false, true);
        handleCloseEditSidebar();
      }
    } catch (e) {
      console.error(e);
    }
    setIsLoading(false);
  }, [editSidebarSelectedItems, editSidebarFormData, editSidebarSelectedUser, getPeriodData]);

  const handleDeleteEditSidebar = useCallback(async () => {
    try {
      setIsLoading(true);
      const payload = {
        assignment_ids: editSidebarSelectedItems.map((item) =>
          item.halfPart === 'first_half'
            ? item.cell.first_half_user?.id
            : item.halfPart === 'second_half'
              ? item.cell.second_half_user?.id
              : item.cell.both_halves_user.id,
        ),
      };
      const response = await bulkDeleteSchedulingCellAssignmentsService(payload);
      if (isResponseOk(response)) {
        getPeriodData(true, false, true);
        handleCloseEditSidebar();
      }
    } catch (e) {
      console.error(e);
    }
    setIsLoading(false);
  }, [editSidebarSelectedItems, getPeriodData]);

  const getOffReason = () => {
    const offDueOptions = [
      { value: 'pto', label: 'PTO' },
      { value: 'call_off', label: 'Call off' },
      { value: 'other', label: 'Other' },
    ];

    if (editSidebarFormData.offDueTo.value === 'other') {
      return editSidebarFormData.offDueTo.otherInputValue;
    }

    return offDueOptions.find(({ value }) => value === editSidebarFormData.offDueTo.value).label;
  };

  const handleOffDueEditSidebar = useCallback(async () => {
    try {
      setIsLoading(true);
      const payload = {
        assignment_ids: editSidebarSelectedItems.map((item) =>
          item.halfPart === 'first_half'
            ? item.cell.first_half_user?.id
            : item.halfPart === 'second_half'
              ? item.cell.second_half_user?.id
              : item.cell.both_halves_user.id,
        ),
        off_reason: getOffReason(),
      };
      const response = await bulkOffDueSchedulingCellAssignmentsService(payload);
      if (isResponseOk(response)) {
        getPeriodData(true, false, true);
        handleCloseEditSidebar();
      }
    } catch (e) {
      console.error(e);
    }
    setIsLoading(false);
  }, [editSidebarSelectedItems, getPeriodData, editSidebarFormData]);

  const sundayDayIndexes = useMemo(() => {
    const indexes = [];
    const startDate = new Date(periodData.start_date);
    const endDate = new Date(periodData.end_date);

    let currentDate = new Date(startDate);
    let counter = 0;

    while (currentDate <= endDate) {
      const dayOfWeek = currentDate.getDay();
      if (dayOfWeek === 0 && counter > 0) {
        indexes.push(counter);
      }
      currentDate.setDate(currentDate.getDate() + 1);
      counter++;
    }

    return indexes;
  }, [periodData]);

  const getPeriodById = useCallback(
    async (periodID) => {
      try {
        const params = {};
        setIsLoading(true);
        const periodResponse = await getPeriodByIdService(periodID, params);
        if (isResponseOk(periodResponse) && periodResponse.data) {
          const periodStartDate = getDateFromString(periodResponse.data.start_date);
          const periodEndDate = getDateFromString(periodResponse.data.end_date);
          setPeriodData({
            ...periodResponse.data,
            start_date: periodStartDate,
            end_date: periodEndDate,
          });
          setSkipRefetching(true);
          const newFiltersDate = periodStartDate;
          setFiltersData((prevState) => {
            return {
              ...prevState,
              date: newFiltersDate,
            };
          });
          const interval = setInterval(() => {
            const shiftElemId = `shift-date-${String(newFiltersDate ? newFiltersDate.getMonth() + 1 : periodStartDate).padStart(2, '0')}-${String(
              newFiltersDate ? newFiltersDate.getDate() : periodStartDate,
            ).padStart(2, '0')}`;
            const element = document.getElementById(shiftElemId);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
              clearInterval(interval);
            }
          }, 500);
        }
      } catch (e) {
        setPeriodData({});
        console.error(e);
      }
      setIsLoading(false);
    },
    [companyID],
  );

  useEffect(() => {
    (async () => {
      try {
        const params = {
          company_id: companyID,
        };
        const facilitiesResponse = await getAvailableFacilitiesService(params);
        if (isResponseOk(facilitiesResponse) && facilitiesResponse.data) {
          setAvailableFacilities(facilitiesResponse.data);
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, [companyID, user.id]);

  useEffect(() => {
    getPeriodData();
  }, [getPeriodData]);

  const facilitiesOptions = useMemo(() => {
    return availableFacilities?.map(({ id, name }) => ({ value: id, label: name }));
  }, [availableFacilities]);

  useEffect(() => {
    if (facilityId && facilitiesOptions?.length) {
      setFiltersData((prevState) => ({
        ...prevState,
        facility: facilitiesOptions.find((item) => item.value === parseInt(facilityId)),
      }));
    }
  }, [facilitiesOptions, facilityId]);

  const handleChangeDate = async (date) => {
    const isDateInPeriod =
      new Date(date).setHours(0, 0, 0, 0) >= new Date(periodData.start_date).setHours(0, 0, 0, 0) &&
      new Date(date).setHours(0, 0, 0, 0) <= new Date(periodData.end_date).setHours(0, 0, 0, 0);

    if (isDateInPeriod) {
      document
        .getElementById(`shift-date-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`)
        .scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }

    setFiltersData((prevState) => ({ ...prevState, date: date }));
  };

  const shiftsData = useMemo(
    () => ({
      1: convertTimeRangeTo12Hour(periodData.shift_1),
      2: convertTimeRangeTo12Hour(periodData.shift_2),
      3: convertTimeRangeTo12Hour(periodData.shift_3),
    }),
    [periodData],
  );

  const shiftRequestCounts = useMemo(() => countShiftRequests(periodData), [periodData]);

  // A shift's column header shows how many pickup requests wait on that day and
  // shift; clicking it opens them all in AssignmentRequestsModal.
  const renderShiftHeader = (dayIndex, shiftOrder, className) => {
    const date = periodData.days?.[dayIndex]?.date;
    const requestsCount = (!showRecurringShifts && shiftRequestCounts[date]?.[shiftOrder]) || 0;
    const canOpenRequests = requestsCount > 0 && hasWritePermission && !isSelectingPostShiftsActive && !editSidebarAction;

    return (
      <th
        className={cx('shift-col-name', className, { 'has-requests': canOpenRequests })}
        onClick={
          canOpenRequests ? () => setAssignmentRequestsModalData({ date, shiftOrder, shiftLabel: shiftsData[shiftOrder] }) : undefined
        }
      >
        {shiftsData[shiftOrder]}
        {requestsCount > 0 && <span className="assignment-requests-badge">{requestsCount}</span>}
      </th>
    );
  };

  const handlePostShifts = useCallback(async () => {
    try {
      const payload = {
        cells: selectedShiftsToPost.map((item) => ({ cell_id: item.cell.id, half_part: item.halfPart })),
      };
      const response = await postShiftsService(periodData.id, payload);
      if (isResponseOk(response)) {
        getPeriodData(true, false, true);
        setSelectedShiftsToPost([]);
        setIsSelectingPostShiftsActive(false);
      }
    } catch (e) {
      console.log(e);
    }
  }, [selectedShiftsToPost]);

  return (
    <Layout
      title={`Scheduling ${filtersData.facility?.label || ''}`}
      className={cx('bedboard-page-layout', {
        isTablet,
        isMobile,
      })}
      breadcrumbs={[
        {
          name: 'Scheduling',
        },
      ]}
    >
      {isLoading && <PageLoader />}
      <div className={cx('page-wrapper scheduling-page', { isMobile, isTablet, isDesktop })}>
        <Header />

        <div className="library-filters">
          <div className="library-filters-wrapper" style={{ alignItems: 'flex-end' }}>
            <div className="form-item">
              <label className="form-label">Facility</label>
              <Select
                options={facilitiesOptions}
                value={filtersData.facility}
                className="form-control-select"
                classNamePrefix="react-select"
                onChange={(e) => {
                  setFiltersData((prevState) => ({ ...prevState, date: null, facility: e }));
                }}
                placeholder="Select Facility"
                isSearchable={false}
              />
            </div>
            <div className="form-item">
              <label className="form-label">Date</label>
              <DateCalendar
                disabled={isEmptySchedulingPeriod || !filtersData.facility}
                selectedDate={filtersData.date}
                className="form-control date-filter__input"
                handleChange={(date) => handleChangeDate(date)}
                placeholderText={'Select date'}
                customInput={<CustomDatePickerInput ref={datePickerInputRef} disabled={isEmptySchedulingPeriod || !filtersData.facility} />}
              />
            </div>
            {!!periodData.id && (
              <>
                <button
                  className={'btn btn-outline pagination-btn'}
                  onClick={() => getPeriodById(periodData.previous_period_id)}
                  disabled={!periodData.previous_period_id}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M12.8334 6.9974H1.16675M1.16675 6.9974L7.00008 12.8307M1.16675 6.9974L7.00008 1.16406"
                      stroke="#344054"
                      strokeWidth="1.67"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Previous 2 Weeks
                </button>
                <button
                  className={'btn btn-outline pagination-btn'}
                  onClick={() => getPeriodById(periodData.next_period_id)}
                  disabled={!periodData.next_period_id}
                >
                  Next 2 Weeks
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M1.16675 6.9974H12.8334M12.8334 6.9974L7.00008 1.16406M12.8334 6.9974L7.00008 12.8307"
                      stroke="#344054"
                      strokeWidth="1.67"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </>
            )}
            {!isMobile && !isPastPeriod && hasWritePermission && (
              <button
                className="btn btn-outline post-shifts-btn"
                onClick={() => setIsSelectingPostShiftsActive(true)}
                disabled={!periodData.id}
              >
                Post Shifts
              </button>
            )}
            {hasWritePermission && periodData.id && !isMobile && (
              <div className="dropdown" ref={scheduleActionRef}>
                <button onClick={() => setScheduleActionDropdownOpen((state) => !state)} className="btn btn-outline schedule-actions-btn">
                  <img src="/images/more-vertical.svg" alt="" />
                </button>
                {scheduleActionDropdownOpen && (
                  <ul className="dropdown-menu schedule-actions-dropdown-menu">
                    {hasWritePermission && !periodData.is_published && (
                      <li>
                        <button
                          className="btn btn-left btn-full btn-text "
                          onClick={() => {
                            setIsPostScheduleModalOpen(true);
                            setScheduleActionDropdownOpen(false);
                          }}
                        >
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M4.54541 1.82031H18.4545C18.6233 1.82031 18.7851 1.89215 18.9045 2.02001C19.0238 2.14788 19.0909 2.3213 19.0909 2.50213V14.5476"
                              stroke="#202024"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M2.72729 12.547V6.10886C2.72729 5.93495 2.791 5.76816 2.90439 5.64519C3.01779 5.52221 3.17158 5.45312 3.33195 5.45312H14.8499C15.0103 5.45312 15.1641 5.52221 15.2775 5.64519C15.3909 5.76816 15.4546 5.93495 15.4546 6.10886V17.5247C15.4546 17.6986 15.3909 17.8654 15.2775 17.9883C15.1641 18.1113 15.0103 18.1804 14.8499 18.1804H8.77377"
                              stroke="#202024"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M9.091 13.6328L3.63645 18.1783L0.90918 15.9055"
                              stroke="#202024"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          Post Schedule
                        </button>
                      </li>
                    )}
                    <li>
                      <button
                        className="btn btn-left btn-full btn-text"
                        onClick={() => {
                          setIsPrintScheduleModalConfirmOpen(true);
                          setScheduleActionDropdownOpen(false);
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <g clipPath="url(#clip0_14762_76280)">
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M4.16658 1.66927C4.16658 1.20903 4.53968 0.835938 4.99992 0.835938H14.9999C15.4602 0.835938 15.8333 1.20903 15.8333 1.66927V6.66927H16.6666C17.3296 6.66927 17.9655 6.93266 18.4344 7.4015C18.9032 7.87034 19.1666 8.50623 19.1666 9.16927V13.3359C19.1666 13.999 18.9032 14.6349 18.4344 15.1037C17.9655 15.5725 17.3296 15.8359 16.6666 15.8359H15.8333V18.3359C15.8333 18.7962 15.4602 19.1693 14.9999 19.1693H4.99992C4.53968 19.1693 4.16658 18.7962 4.16658 18.3359V15.8359H3.33325C2.67021 15.8359 2.03433 15.5725 1.56548 15.1037C1.09664 14.6349 0.833252 13.999 0.833252 13.3359V9.16927C0.833252 8.50623 1.09664 7.87034 1.56548 7.4015C2.03433 6.93266 2.67021 6.66927 3.33325 6.66927H4.16658V1.66927ZM5.83325 17.5026H14.1666V12.5026H5.83325V17.5026ZM15.8333 14.1693V11.6693C15.8333 11.209 15.4602 10.8359 14.9999 10.8359H4.99992C4.53968 10.8359 4.16658 11.209 4.16658 11.6693V14.1693H3.33325C3.11224 14.1693 2.90028 14.0815 2.744 13.9252C2.58772 13.7689 2.49992 13.557 2.49992 13.3359V9.16927C2.49992 8.94826 2.58772 8.73629 2.744 8.58001C2.90028 8.42373 3.11224 8.33594 3.33325 8.33594H16.6666C16.8876 8.33594 17.0996 8.42373 17.2558 8.58001C17.4121 8.7363 17.4999 8.94826 17.4999 9.16927V13.3359C17.4999 13.5569 17.4121 13.7689 17.2558 13.9252C17.0996 14.0815 16.8876 14.1693 16.6666 14.1693H15.8333ZM14.1666 2.5026V6.66927H5.83325V2.5026H14.1666Z"
                              fill="#101828"
                            />
                          </g>
                          <defs>
                            <clipPath id="clip0_14762_76280">
                              <rect width="20" height="20" fill="white" />
                            </clipPath>
                          </defs>
                        </svg>
                        Print Weekly Schedule
                      </button>
                    </li>
                    <li>
                      <button
                        className="btn btn-left btn-full btn-text"
                        onClick={() => {
                          setIsPrintEmployeeScheduleSelectOpen(true);
                          setScheduleActionDropdownOpen(false);
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <g clipPath="url(#clip0_emp_menu_icon)">
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M4.16658 1.66927C4.16658 1.20903 4.53968 0.835938 4.99992 0.835938H14.9999C15.4602 0.835938 15.8333 1.20903 15.8333 1.66927V6.66927H16.6666C17.3296 6.66927 17.9655 6.93266 18.4344 7.4015C18.9032 7.87034 19.1666 8.50623 19.1666 9.16927V13.3359C19.1666 13.999 18.9032 14.6349 18.4344 15.1037C17.9655 15.5725 17.3296 15.8359 16.6666 15.8359H15.8333V18.3359C15.8333 18.7962 15.4602 19.1693 14.9999 19.1693H4.99992C4.53968 19.1693 4.16658 18.7962 4.16658 18.3359V15.8359H3.33325C2.67021 15.8359 2.03433 15.5725 1.56548 15.1037C1.09664 14.6349 0.833252 13.999 0.833252 13.3359V9.16927C0.833252 8.50623 1.09664 7.87034 1.56548 7.4015C2.03433 6.93266 2.67021 6.66927 3.33325 6.66927H4.16658V1.66927ZM5.83325 17.5026H14.1666V12.5026H5.83325V17.5026ZM15.8333 14.1693V11.6693C15.8333 11.209 15.4602 10.8359 14.9999 10.8359H4.99992C4.53968 10.8359 4.16658 11.209 4.16658 11.6693V14.1693H3.33325C3.11224 14.1693 2.90028 14.0815 2.744 13.9252C2.58772 13.7689 2.49992 13.557 2.49992 13.3359V9.16927C2.49992 8.94826 2.58772 8.73629 2.744 8.58001C2.90028 8.42373 3.11224 8.33594 3.33325 8.33594H16.6666C16.8876 8.33594 17.0996 8.42373 17.2558 8.58001C17.4121 8.7363 17.4999 8.94826 17.4999 9.16927V13.3359C17.4999 13.5569 17.4121 13.7689 17.2558 13.9252C17.0996 14.0815 16.8876 14.1693 16.6666 14.1693H15.8333ZM14.1666 2.5026V6.66927H5.83325V2.5026H14.1666Z"
                              fill="#101828"
                            />
                          </g>
                          <defs>
                            <clipPath id="clip0_emp_menu_icon">
                              <rect width="20" height="20" fill="white" />
                            </clipPath>
                          </defs>
                        </svg>
                        Print Employee Schedule
                      </button>
                    </li>
                    <li>
                      <button
                        className="btn btn-left btn-full btn-text "
                        onClick={() => {
                          setIsNotificationSettingsModalOpen(true);
                          setScheduleActionDropdownOpen(false);
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <g clipPath="url(#clip0_14683_17208)">
                            <path
                              d="M10.0002 12.5026C11.3809 12.5026 12.5002 11.3833 12.5002 10.0026C12.5002 8.62189 11.3809 7.5026 10.0002 7.5026C8.61945 7.5026 7.50016 8.62189 7.50016 10.0026C7.50016 11.3833 8.61945 12.5026 10.0002 12.5026Z"
                              stroke="#101828"
                              strokeWidth="1.67"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M16.1668 12.5026C16.0559 12.754 16.0228 13.0328 16.0718 13.3031C16.1208 13.5734 16.2497 13.8229 16.4418 14.0193L16.4918 14.0693C16.6468 14.2241 16.7697 14.4079 16.8536 14.6102C16.9375 14.8125 16.9806 15.0294 16.9806 15.2484C16.9806 15.4675 16.9375 15.6843 16.8536 15.8867C16.7697 16.089 16.6468 16.2728 16.4918 16.4276C16.337 16.5826 16.1532 16.7055 15.9509 16.7894C15.7486 16.8732 15.5317 16.9164 15.3127 16.9164C15.0936 16.9164 14.8768 16.8732 14.6744 16.7894C14.4721 16.7055 14.2883 16.5826 14.1335 16.4276L14.0835 16.3776C13.8871 16.1855 13.6376 16.0566 13.3673 16.0076C13.097 15.9586 12.8182 15.9917 12.5668 16.1026C12.3204 16.2082 12.1101 16.3836 11.9621 16.6072C11.814 16.8308 11.7346 17.0928 11.7335 17.3609V17.5026C11.7335 17.9446 11.5579 18.3686 11.2453 18.6811C10.9328 18.9937 10.5089 19.1693 10.0668 19.1693C9.6248 19.1693 9.20088 18.9937 8.88832 18.6811C8.57576 18.3686 8.40016 17.9446 8.40016 17.5026V17.4276C8.39371 17.1518 8.30443 16.8843 8.14392 16.6599C7.98342 16.4354 7.75911 16.2645 7.50016 16.1693C7.24882 16.0583 6.97 16.0253 6.69967 16.0743C6.42934 16.1233 6.1799 16.2522 5.9835 16.4443L5.9335 16.4943C5.77871 16.6492 5.59489 16.7722 5.39256 16.856C5.19023 16.9399 4.97336 16.9831 4.75433 16.9831C4.5353 16.9831 4.31843 16.9399 4.1161 16.856C3.91377 16.7722 3.72995 16.6492 3.57516 16.4943C3.4202 16.3395 3.29727 16.1557 3.2134 15.9533C3.12952 15.751 3.08635 15.5341 3.08635 15.3151C3.08635 15.0961 3.12952 14.8792 3.2134 14.6769C3.29727 14.4745 3.4202 14.2907 3.57516 14.1359L3.62516 14.0859C3.81728 13.8895 3.94615 13.6401 3.99517 13.3698C4.04418 13.0994 4.01109 12.8206 3.90016 12.5693C3.79453 12.3228 3.61913 12.1126 3.39555 11.9645C3.17198 11.8165 2.90999 11.737 2.64183 11.7359H2.50016C2.05814 11.7359 1.63421 11.5603 1.32165 11.2478C1.00909 10.9352 0.833496 10.5113 0.833496 10.0693C0.833496 9.62724 1.00909 9.20332 1.32165 8.89076C1.63421 8.5782 2.05814 8.4026 2.50016 8.4026H2.57516C2.85099 8.39615 3.1185 8.30687 3.34291 8.14636C3.56732 7.98586 3.73826 7.76155 3.8335 7.5026C3.94443 7.25126 3.97752 6.97244 3.9285 6.70211C3.87948 6.43178 3.75061 6.18234 3.5585 5.98594L3.5085 5.93594C3.35354 5.78115 3.2306 5.59733 3.14673 5.395C3.06286 5.19267 3.01969 4.9758 3.01969 4.75677C3.01969 4.53775 3.06286 4.32087 3.14673 4.11854C3.2306 3.91621 3.35354 3.73239 3.5085 3.5776C3.66328 3.42264 3.8471 3.29971 4.04943 3.21584C4.25176 3.13196 4.46864 3.08879 4.68766 3.08879C4.90669 3.08879 5.12357 3.13196 5.3259 3.21584C5.52823 3.29971 5.71204 3.42264 5.86683 3.5776L5.91683 3.6276C6.11323 3.81972 6.36268 3.94859 6.63301 3.99761C6.90333 4.04662 7.18215 4.01353 7.4335 3.9026H7.50016C7.74664 3.79697 7.95684 3.62157 8.10491 3.39799C8.25297 3.17442 8.33243 2.91243 8.3335 2.64427V2.5026C8.3335 2.06058 8.50909 1.63665 8.82165 1.32409C9.13421 1.01153 9.55814 0.835938 10.0002 0.835938C10.4422 0.835938 10.8661 1.01153 11.1787 1.32409C11.4912 1.63665 11.6668 2.06058 11.6668 2.5026V2.5776C11.6679 2.84576 11.7474 3.10775 11.8954 3.33133C12.0435 3.5549 12.2537 3.7303 12.5002 3.83594C12.7515 3.94687 13.0303 3.97996 13.3007 3.93094C13.571 3.88193 13.8204 3.75305 14.0168 3.56094L14.0668 3.51094C14.2216 3.35598 14.4054 3.23305 14.6078 3.14917C14.8101 3.0653 15.027 3.02213 15.246 3.02213C15.465 3.02213 15.6819 3.0653 15.8842 3.14917C16.0866 3.23305 16.2704 3.35598 16.4252 3.51094C16.5801 3.66573 16.7031 3.84954 16.7869 4.05187C16.8708 4.2542 16.914 4.47108 16.914 4.6901C16.914 4.90913 16.8708 5.12601 16.7869 5.32834C16.7031 5.53067 16.5801 5.71448 16.4252 5.86927L16.3752 5.91927C16.183 6.11567 16.0542 6.36512 16.0052 6.63545C15.9561 6.90578 15.9892 7.18459 16.1002 7.43594V7.5026C16.2058 7.74908 16.3812 7.95929 16.6048 8.10735C16.8284 8.25541 17.0903 8.33487 17.3585 8.33594H17.5002C17.9422 8.33594 18.3661 8.51153 18.6787 8.82409C18.9912 9.13665 19.1668 9.56058 19.1668 10.0026C19.1668 10.4446 18.9912 10.8686 18.6787 11.1811C18.3661 11.4937 17.9422 11.6693 17.5002 11.6693H17.4252C17.157 11.6703 16.895 11.7498 16.6714 11.8979C16.4479 12.0459 16.2725 12.2561 16.1668 12.5026Z"
                              stroke="#101828"
                              strokeWidth="1.67"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </g>
                          <defs>
                            <clipPath id="clip0_14683_17208">
                              <rect width="20" height="20" fill="white" />
                            </clipPath>
                          </defs>
                        </svg>
                        Notification Settings
                      </button>
                    </li>

                    {isSuperAdmin() && (
                      <li>
                        <button
                          className="btn btn-left btn-full btn-text "
                          onClick={() => {
                            setIsOpenResetScheduleModal(true);
                            setScheduleActionDropdownOpen(false);
                          }}
                        >
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g clipPath="url(#clip0_14490_17183)">
                              <path
                                d="M19.1668 3.33136V8.33136M19.1668 8.33136H14.1668M19.1668 8.33136L15.3002 4.69803C14.4045 3.80196 13.2965 3.14737 12.0795 2.79534C10.8625 2.44331 9.57608 2.40532 8.3404 2.68491C7.10472 2.9645 5.96 3.55256 5.01305 4.39421C4.06611 5.23586 3.3478 6.30368 2.92516 7.49803M0.833496 16.6647V11.6647M0.833496 11.6647H5.8335M0.833496 11.6647L4.70016 15.298C5.59579 16.1941 6.70381 16.8487 7.92084 17.2007C9.13787 17.5527 10.4242 17.5907 11.6599 17.3111C12.8956 17.0316 14.0403 16.4435 14.9873 15.6018C15.9342 14.7602 16.6525 13.6924 17.0752 12.498"
                                stroke="#101828"
                                strokeWidth="1.67"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_14490_17183">
                                <rect width="20" height="20" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>
                          Reset Schedule
                        </button>
                      </li>
                    )}
                    {periodData.was_posted && (
                      <li>
                        <button
                          className="btn btn-left btn-full btn-text "
                          onClick={() => {
                            setIsOpenCancelPostedShiftsModal(true);
                            setScheduleActionDropdownOpen(false);
                          }}
                          style={{ color: '#B42318' }}
                        >
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g clipPath="url(#clip0_14553_72532)">
                              <path
                                d="M12.4998 7.4974L7.49984 12.4974M7.49984 7.4974L12.4998 12.4974M18.3332 9.9974C18.3332 14.5998 14.6022 18.3307 9.99984 18.3307C5.39746 18.3307 1.6665 14.5998 1.6665 9.9974C1.6665 5.39502 5.39746 1.66406 9.99984 1.66406C14.6022 1.66406 18.3332 5.39502 18.3332 9.9974Z"
                                stroke="#B42318"
                                strokeWidth="1.67"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_14553_72532">
                                <rect width="20" height="20" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>
                          Сancel All Posted Shifts
                        </button>
                      </li>
                    )}
                  </ul>
                )}
              </div>
            )}
          </div>

          {!isMobile && !!periodData.id && hasWritePermission && (
            <div className="form-item" style={{ marginLeft: 'auto', width: 'unset' }}>
              <button className="btn btn-outline edit-schedule-btn" onClick={() => setIsEditSidebarOpen(true)}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_15418_7572)">
                    <path
                      d="M9.1665 3.3332H3.33317C2.89114 3.3332 2.46722 3.50879 2.15466 3.82135C1.8421 4.13391 1.6665 4.55784 1.6665 4.99986V16.6665C1.6665 17.1086 1.8421 17.5325 2.15466 17.845C2.46722 18.1576 2.89114 18.3332 3.33317 18.3332H14.9998C15.4419 18.3332 15.8658 18.1576 16.1783 17.845C16.4909 17.5325 16.6665 17.1086 16.6665 16.6665V10.8332M15.4165 2.0832C15.748 1.75168 16.1977 1.56543 16.6665 1.56543C17.1353 1.56543 17.585 1.75168 17.9165 2.0832C18.248 2.41472 18.4343 2.86436 18.4343 3.3332C18.4343 3.80204 18.248 4.25168 17.9165 4.5832L9.99984 12.4999L6.6665 13.3332L7.49984 9.99986L15.4165 2.0832Z"
                      stroke="#344054"
                      strokeWidth="1.67"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_15418_7572">
                      <rect width="20" height="20" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                Edit
              </button>
            </div>
          )}
        </div>

        <div className={'table-container'} style={isEditSidebarOpen ? { width: 'calc(100% - 355px)' } : {}}>
          {isEmptySchedulingPeriod ? (
            <EmptySchedulingPeriod
              periodData={periodData}
              setFiltersData={setFiltersData}
              setPeriodData={setPeriodData}
              setIsEmptySchedulingPeriod={setIsEmptySchedulingPeriod}
            />
          ) : periodData.id ? (
            <table border="1" cellPadding="8" cellSpacing="0" className="scheduling-table">
              <thead>
                <tr>
                  <th colSpan="2" className="unit-info-col"></th>
                  {periodData?.days?.map((periodDay, index) => (
                    <SchedulingDay
                      key={index}
                      periodDay={periodDay}
                      periodData={periodData}
                      index={index}
                      handleClickPrintSchedule={handleClickPrintSchedule}
                      isPastPeriod={isPastPeriod}
                      setPeriodData={setPeriodData}
                    />
                  ))}
                </tr>
                <tr className="shifts-row">
                  <th colSpan="2" className="unit-info-col"></th>
                  {Array.from({ length: 14 }, (_, i) => (
                    <>
                      {renderShiftHeader(i, 1, cx('first-in-date', { 'has-red-border': sundayDayIndexes?.includes(i) }))}
                      {renderShiftHeader(i, 2)}
                      {renderShiftHeader(i, 3)}
                    </>
                  ))}
                </tr>
              </thead>
              <tbody>
                <DndProvider backend={HTML5Backend}>
                  {periodData.units.map((unit, index) => (
                    <SchedulingUnitRow
                      editSidebarAction={editSidebarAction}
                      editSidebarFormData={editSidebarFormData}
                      editSidebarSelectedItems={editSidebarSelectedItems}
                      editSidebarSelectedUser={editSidebarSelectedUser}
                      setEditSidebarSelectedItems={setEditSidebarSelectedItems}
                      getPeriodData={getPeriodData}
                      unit={unit}
                      periodData={periodData}
                      key={`${unit.name}-${unit.id}-${index}`}
                      unitIndex={index}
                      shiftsData={shiftsData}
                      handleAddUnitRole={handleAddUnitRole}
                      handleUpdateUnitRole={handleUpdateUnitRole}
                      setPeriodData={setPeriodData}
                      roleLabels={roleLabels}
                      isPastPeriod={isPastPeriod}
                      showRecurringShifts={showRecurringShifts}
                      cellAssignmentData={cellAssignmentData}
                      setCellAssignmentData={setCellAssignmentData}
                      isSelectingPostShiftsActive={isSelectingPostShiftsActive}
                      setSelectedShiftsToPost={setSelectedShiftsToPost}
                      selectedShiftsToPost={selectedShiftsToPost}
                    />
                  ))}
                  <tr className="notes-row">
                    <td colSpan={2} className="notes-title-col">
                      <div className="notes-title-wrapper">Notes</div>
                    </td>
                    {periodData?.days?.map((periodDay, index) => (
                      <SchedulingDayNotes
                        key={`${periodDay.date}-${index}`}
                        day={periodDay}
                        isPastPeriod={isPastPeriod}
                        setPeriodData={setPeriodData}
                        hasRedBorder={sundayDayIndexes?.includes(index)}
                      />
                    ))}
                  </tr>
                </DndProvider>
              </tbody>
            </table>
          ) : !filtersData.facility ? (
            <div className="empty-scheduling-page">Please select a facility to see scheduling details</div>
          ) : (
            <></>
          )}
        </div>
        {isSelectingPostShiftsActive && (
          <PostShiftsSidebar
            handleClose={() => {
              setIsSelectingPostShiftsActive(false);
              setSelectedShiftsToPost([]);
            }}
            setSelectedShiftsToPost={setSelectedShiftsToPost}
            selectedShiftsToPost={selectedShiftsToPost}
            handlePost={handlePostShifts}
          />
        )}
        {!!printScheduleData && (
          <ExportScheduleToPdfModal
            onClose={() => setPrintScheduleData(null)}
            facilityName={filtersData.facility.label}
            data={printScheduleData}
            shiftsData={shiftsData}
          />
        )}
        {isOpenResetScheduleModal && (
          <ResetScheduleModal closeModal={() => setIsOpenResetScheduleModal(false)} handleConfirm={handleResetSchedule} />
        )}
        {isExportRecurringShiftsModalOpen && (
          <ExportRecurringScheduleModal
            onClose={() => setIsExportRecurringShiftsModalOpen(false)}
            facilityName={filtersData.facility.label}
            periodData={periodData}
            shiftsData={shiftsData}
          />
        )}
        {isOpenCancelPostedShiftsModal && (
          <CancelPostedShiftsModal closeModal={() => setIsOpenCancelPostedShiftsModal(false)} handleConfirm={handleCancelPostedShifts} />
        )}
        {!!cellAssignmentData?.unit && (
          <CellAssignmentSidebar
            setPeriodData={setPeriodData}
            data={cellAssignmentData}
            employees={employees}
            handleClose={() => setCellAssignmentData({})}
            assignmentRequestModalRef={assignmentRequestModalRef}
            nurseHours={periodData.nurse_hours_per_shift}
            nurseMinutes={periodData.nurse_minutes_per_shift}
            cnaHours={periodData.cna_hours_per_shift}
            cnaMinutes={periodData.cna_minutes_per_shift}
          />
        )}
        {!!assignmentRequestsModalData.date && (
          <AssignmentRequestsModal
            closeModal={() => setAssignmentRequestsModalData({})}
            data={assignmentRequestsModalData}
            setPeriodData={setPeriodData}
            periodData={periodData}
            roleLabels={roleLabels}
            setCellAssignmentData={setCellAssignmentData}
            ref={assignmentRequestModalRef}
          />
        )}
        {isNotificationSettingsModalOpen && (
          <NotificationSettingsModal
            closeModal={() => setIsNotificationSettingsModalOpen(false)}
            periodData={periodData}
            setPeriodData={setPeriodData}
          />
        )}
        {isPostScheduleModalOpen && (
          <PostScheduleModal closeModal={() => setIsPostScheduleModalOpen(false)} periodData={periodData} setPeriodData={setPeriodData} />
        )}
        {isPrintScheduleConfirmModalOpen && (
          <PrintWeeklyScheduleConfirmModal
            closeModal={() => setIsPrintScheduleModalConfirmOpen(false)}
            periodData={periodData}
            setPrintScheduleDays={setPrintScheduleDays}
          />
        )}
        {printScheduleDays?.length > 0 && (
          <PrintWeeklyScheduleModal
            onClose={() => setPrintScheduleDays([])}
            periodData={periodData}
            printScheduleDays={printScheduleDays}
            shiftsData={shiftsData}
            sundayDayIndexes={sundayDayIndexes}
          />
        )}
        {isPrintEmployeeScheduleSelectOpen && (
          <PrintEmployeeScheduleSelectModal
            employees={employees}
            periodData={periodData}
            companyId={companyID}
            facilityId={filtersData.facility?.value}
            closeModal={() => setIsPrintEmployeeScheduleSelectOpen(false)}
            onConfirm={(employee, printPeriodData) => setPrintEmployeeSchedule({ employee, periodData: printPeriodData })}
          />
        )}
        {!!printEmployeeSchedule && (
          <PrintEmployeeScheduleModal
            onClose={() => setPrintEmployeeSchedule(null)}
            periodData={printEmployeeSchedule.periodData}
            employee={printEmployeeSchedule.employee}
          />
        )}

        {isEditSidebarOpen && (
          <EditSidebar
            editSidebarSelectedItems={editSidebarSelectedItems}
            setEditSidebarSelectedItems={setEditSidebarSelectedItems}
            handleClose={handleCloseEditSidebar}
            employees={employees}
            setPeriodData={setPeriodData}
            formData={editSidebarFormData}
            setFormData={setEditSidebarFormData}
            selectedUser={editSidebarSelectedUser}
            setSelectedUser={setEditSidebarSelectedUser}
            setSidebarAction={setEditSidebarAction}
            sidebarAction={editSidebarAction}
            handleSave={handleSaveEditSidebar}
            handleDelete={handleDeleteEditSidebar}
            handleOffDueEditSidebar={handleOffDueEditSidebar}
            isSavingUser={isLoading}
          />
        )}
      </div>
    </Layout>
  );
};

export default SchedulingPage;
